import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { LexicalEditor as LexicalEditorType } from 'lexical';
import {
  Mutation,
  MutationConversationSendInstagramMessageArgs,
  ContactInteractionChannelAvailabilityForContactResourceToContactAddressAvailability,
  ContactInteractionChannelResourceUnion,
} from '@lgg/isomorphic/types/__generated__/graphql';
import {
  ActiveAiAgentBanner,
  useHasActiveAiAgent,
} from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/active-ai-agent-banner';
import { ContactInteractionInputAreaCharacterCounter } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/contact-interaction-input-area-character-counter';
import { ContactInteractionInputManagerContext } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/contact-interaction-input-manager';
import { ContactInteractionsReplyModeInfoMessage } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/contact-interactions-reply-mode-message';
import { CustomEditor } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/editors/custom-editor';
import {
  CustomEditorProps,
  useHandleReplyError,
} from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/editors/helpers';
import { CONVERSATION_SEND_INSTAGRAM_MESSAGE } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/graphql-operations';
import { ReplyWindowBar } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/reply-window-bar';
import { useAuthorization } from 'src/hooks/use-authorization';

export const InstagramEditor = memo<CustomEditorProps>(
  ({ handleFetchNewMessageCompleted, channelAvailability, availabilityMessageRef }) => {
    const { messageValues, attachmentList, conversationId } = useContext(
      ContactInteractionInputManagerContext,
    );

    const handleReplyError = useHandleReplyError();
    const hasActiveAiAgent = useHasActiveAiAgent();
    const { getFeatureFlag } = useAuthorization();

    const { t } = useTranslation(['conversations']);
    const isAiToolsFeatureEnabled = getFeatureFlag('AI');
    const isMessageTemplatesFeatureEnabled = getFeatureFlag(
      'contact-interaction-templates',
    );
    const [selectedResource, setSelectedResource] =
      useState<ContactInteractionChannelResourceUnion | null>(null);
    const [selectedResourceAvailability, setSelectedResourceAvailability] = useState<
      | ContactInteractionChannelAvailabilityForContactResourceToContactAddressAvailability
      | undefined
    >(undefined);
    const [sendInstagramMessage, { loading: isSendingInstagramMessage }] = useMutation<
      Pick<Mutation, 'conversationSendInstagramMessage'>,
      MutationConversationSendInstagramMessageArgs
    >(CONVERSATION_SEND_INSTAGRAM_MESSAGE, {
      onError: handleReplyError,
    });

    const isContactWindowOpen = useMemo(
      () =>
        !!selectedResourceAvailability &&
        selectedResourceAvailability.unavailableReasonCode !== 'CHAT_WINDOW_CLOSED',
      [selectedResourceAvailability],
    );

    const isInputEnabled = useMemo(
      () =>
        !hasActiveAiAgent && isContactWindowOpen && !!channelAvailability?.isAvailable,
      [channelAvailability?.isAvailable, hasActiveAiAgent, isContactWindowOpen],
    );

    useEffect(() => {
      if (channelAvailability) {
        setSelectedResourceAvailability(
          channelAvailability.resourceToContactAddressAvailability?.find(
            (resource) => resource.resourceId === selectedResource?.id,
          ),
        );
      }
    }, [channelAvailability, selectedResource?.id]);

    useEffect(() => {
      if (!channelAvailability) {
        return;
      }
      const { resources, lastUsedResourceId } = channelAvailability;

      const defaultSelectedResource =
        resources.find(({ id }) => id === lastUsedResourceId) ?? resources[0] ?? null;

      setSelectedResource(defaultSelectedResource);
    }, [channelAvailability]);

    const handleSendMessage = useCallback(
      async (editor: LexicalEditorType) => {
        if (
          !conversationId ||
          !selectedResourceAvailability ||
          isSendingInstagramMessage
        ) {
          return;
        }

        let commonVariables: Pick<
          MutationConversationSendInstagramMessageArgs,
          'conversationId' | 'message' | 'attachments'
        > = {
          conversationId,
          message: messageValues.message ?? '',
          attachments: [],
        };

        const attachments = attachmentList.map(({ url }) => ({
          s3UploadedUrl: url ?? '',
        }));

        if (attachments.length > 0) {
          commonVariables = {
            ...commonVariables,
            attachments,
          };
        }

        await sendInstagramMessage({
          variables: {
            ...commonVariables,
            resourceId: selectedResourceAvailability.resourceId,
          },
          onCompleted: async ({ conversationSendInstagramMessage: data }) => {
            await handleFetchNewMessageCompleted({
              editor,
              where: {
                contactInteractionId: {
                  eq: Number(data.interaction.id),
                },
              },
            });
          },
        });
      },
      [
        isSendingInstagramMessage,
        conversationId,
        messageValues.message,
        attachmentList,
        selectedResourceAvailability,
        sendInstagramMessage,
        handleFetchNewMessageCompleted,
      ],
    );

    const topContent = useMemo(() => {
      if (hasActiveAiAgent) {
        return <ActiveAiAgentBanner />;
      }

      if (isContactWindowOpen) {
        return null;
      }

      return (
        <>
          <ContactInteractionsReplyModeInfoMessage
            title={t(
              'conversations:messageInput.modes.reply.messages.instagram.messageWindowClosed.title',
            )}
            body={t(
              'conversations:messageInput.modes.reply.messages.instagram.messageWindowClosed.body',
            )}
            lggId="instagram-message-window-closed-info-message"
          />
        </>
      );
    }, [hasActiveAiAgent, isContactWindowOpen, t]);

    const replyWindowBar = useMemo(() => {
      return isContactWindowOpen && availabilityMessageRef?.current
        ? createPortal(
            <ReplyWindowBar
              expirationDate={selectedResourceAvailability?.expiresAt}
              type="INSTAGRAM"
            />,
            availabilityMessageRef.current,
          )
        : null;
    }, [
      availabilityMessageRef,
      isContactWindowOpen,
      selectedResourceAvailability?.expiresAt,
    ]);

    return (
      <>
        {replyWindowBar}
        <CustomEditor
          handleSubmission={handleSendMessage}
          isSubmitting={isSendingInstagramMessage}
          bottomContent={{
            visible: true,
          }}
          topContent={topContent}
          pluginConfig={{
            emojiPicker: { visible: true },
            messageAttachments: { visible: true },
            aiTools: { visible: isAiToolsFeatureEnabled },
            messageTemplates: {
              visible: isMessageTemplatesFeatureEnabled,
            },
            inv360: { visible: true },
          }}
          isInputEnabled={isInputEnabled}
          rightRailLeadingContent={<ContactInteractionInputAreaCharacterCounter />}
        />
      </>
    );
  },
);
